import * as Utils from './utils';
import { FormField } from './form-field.model';
import { FormPermission, FormPermissionFilter } from './form-permission.model';

export interface PermissionFilter {
    state: string;
    role: string;
}

export class Form {
    private static ID_PREFIX = 'wf-';
    private _fields: FormField[];
    public get fields(): FormField[] {
        return this._fields;
    }

    public readonly id: string;

    public readonly name: string;

    private _permissions: FormPermission[] = [];
    public get permissions(): FormPermission[] {
        return this._permissions;
    }

    constructor(id: string | undefined,
                name: string,
                fields: FormField[] = [],
                permissions: FormPermission[] = []) {
                this.id = id ?? `${Form.ID_PREFIX}${Utils.randomString()}`;
                this.name = name;
                this._fields = fields;
                this._permissions = permissions;
    }

    /**
     * Shallow coppy of the Form
     */
    public copy(): Form {
        return new Form(this.id, this.name, this.fields, this.permissions);
    }

    //
    // Fields
    //

    public findFieldByID(id: string): FormField | undefined {
        return this._fields.find(f => f.id === id);
    }

    public findFieldIndexByID(id: string): number {
        return this._fields.findIndex(f => f.id === id);
    }

    public findFieldsByPermissionFilter(filter: PermissionFilter): FormField[] {
        let permissions =
            this.permissions.filter(p => p.role === filter.role);
        permissions =
            permissions.filter(p => p.state === filter.state);

        const ids = permissions.map(p => p.object);
        return this._fields.filter(field => {
            return ids.find(id => id === field.id) ? true : false;
        });
    }

    /**
     * Deletes the form field
     *
     * This method also deletes ALL permissions for the form field.
     *
     * @param value Field ID or instance to be deleted
     */
    public deleteField(value: string | FormField): void {
        const id = typeof value === 'string' ? value : value.id;
        const fields = this.fields.filter(f => f.id !== id);
        this._fields = fields;

        this.deletePermissionsFor(id);
    }

    public addFieldAt(value: FormField, at: number = -1): void {
        const index = at === -1 ? this.fields.length + 1 : at;
        if (index < 0) {
            throw new RangeError(`At argument '${at}' out of bounds!`);
        }

        const fields = [...this.fields];
        fields.splice(index, 0, value);
        this._fields = fields;
    }

    public moveField(from: number, to: number): void {
        if (from < 0 || from > this.fields.length) {
            throw new RangeError($localize `From argument '${from}' out of bounds!`);
        }
        if (to < 0 || to > this.fields.length) {
            throw new RangeError($localize `To argument '${to}' out of bounds!`);
        }

        const fields = [...this.fields];
        fields.splice(to, 0, fields.splice(from, 1)[0]);
        this._fields = fields;
    }

    public patchField(id: string, value: Partial<FormField>): void {
        const index = this.fields.findIndex(f => f.id === id);
        if (index === -1) {
            throw Error(`Can't find field with specified ID '${id}'!`);
        }

        const fields = [...this.fields];
        fields.splice(index, 1, fields[index].patch(value));
        this._fields = fields;
    }

    //
    // Permissions
    //

    public findPermissionsBy(fieldID?: string,
                             roleID?: string,
                             stateID?: string): FormPermission[] {
        const filter = new FormPermissionFilter(fieldID, roleID, stateID);
        return this.permissions.filter(filter.matchingPredicate);
    }

    /**
     * Sets of Updates permissions in bulk
     *
     * @see setPermission()
     * @param permissions List of permissions to set or update
     */
    public setPermissions(permissions: FormPermission[]): void {
        permissions.forEach(permission => this.setPermission(permission));
    }

    /**
     * Sets or Updates permission
     *
     * Method looks for permission with for the same Field, Role
     * and State as the provided argument. If there is such permission
     * it updates the permission otherwise it inserts new permission.
     *
     * @param permission Permission to be set or updated
     */
    public setPermission(permission: FormPermission): void {
        const permissions = [...this.permissions];
        const index = permissions.findIndex(p => {
            return p.object === permission.object &&
                   p.role === permission.role &&
                   p.state === permission.state;
        });
        index === -1
                  ? permissions.push(permission)
                  : permissions.splice(index, 1, permission);

        this._permissions = permissions;
    }

    /**
     * Deletes specific permission
     *
     * @param permission Permission to be deleted
     */
    public deletePermission(permission: FormPermission): FormPermission[] {
        return this.deletePermissionsFor(permission.object,
                                         permission.role,
                                         permission.state);
    }

    public deletePermissionsFor(fieldID?: string,
                                roleID?: string,
                                stateID?: string): FormPermission[] {
        const filter =
            new FormPermissionFilter(fieldID, roleID, stateID);

        const deleted: FormPermission[] =
            this.permissions.filter(filter.matchingPredicate);

        this._permissions =
            this.permissions.filter(filter.notMatchingPredicate);

        return deleted;
    }
}
